import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Проверяем статус платежа...');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const retryTimeoutRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  const getUserId = () => {
    try {
      const storedUserData = localStorage.getItem('user_data');
      if (storedUserData) {
        const parsed = JSON.parse(storedUserData);
        return parsed?.id || parsed?.user_id || parsed?.userId || null;
      }
    } catch (error) {
      console.warn('Не удалось получить user_id из localStorage', error);
    }
    return null;
  };

  useEffect(() => {
    const userId = getUserId();
    const paymentId = searchParams.get('payment_id');

    if (!paymentId) {
      setStatus('error');
      setMessage('Идентификатор платежа не найден. Если оплату удалось завершить, проверьте раздел "Платежи" в личном кабинете.');
      return;
    }

    let isUnmounted = false;

    const checkStatus = async () => {
      try {
        const queryUserId = userId ? `?user_id=${userId}` : '';
        const response = await fetch(`/api/v1/billing/payments/${paymentId}/status${queryUserId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (isUnmounted) {
          return;
        }

        if (!response.ok) {
          throw new Error(data?.detail || `HTTP ${response.status}`);
        }

        setPaymentInfo(data);

        switch (data.status) {
          case 'succeeded':
            setStatus('success');
            setMessage('Платеж успешно завершен. Подписка активирована.');
            redirectTimeoutRef.current = setTimeout(() => {
              // Перезагружаем страницу для обновления состояния подписки
              window.location.href = '/';
            }, 3500);
            break;
          case 'pending':
            setStatus('pending');
            setMessage('Платеж обрабатывается... это может занять до пары минут.');
            retryTimeoutRef.current = setTimeout(checkStatus, 5000);
            break;
          case 'cancelled':
          case 'failed':
            setStatus('error');
            setMessage('Платеж не был завершен. Вы можете попробовать повторить оплату в разделе "Платежи".');
            break;
          default:
            setStatus('error');
            setMessage(`Неизвестный статус платежа: ${data.status}. Проверьте раздел "Платежи" или обратитесь в поддержку.`);
            break;
        }
      } catch (error) {
        if (isUnmounted) {
          return;
        }
        console.error('Ошибка проверки статуса платежа:', error);
        setStatus('error');
        setMessage(`Ошибка при проверке платежа: ${error.message}`);
      }
    };

    checkStatus();

    return () => {
      isUnmounted = true;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [navigate, searchParams]);

  const handleGoToDashboard = () => {
    // Перезагружаем страницу для обновления состояния подписки
    window.location.href = '/';
  };

  return (
    <div className="payment-success-page">
      <div className="payment-success-card">
        {status === 'loading' && (
          <div className="payment-state">
            <div className="payment-icon spinner" />
            <h2>Проверяем платеж</h2>
            <p>{message}</p>
          </div>
        )}

        {status === 'pending' && (
          <div className="payment-state">
            <div className="payment-icon pending">⏳</div>
            <h2>Платеж обрабатывается</h2>
            <p>{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="payment-state">
            <div className="payment-icon success">✅</div>
            <h2>Платеж успешно завершен</h2>
            <p>{message}</p>
            {paymentInfo && (
              <div className="payment-details">
                <div className="detail-row">
                  <span className="detail-label">Сумма</span>
                  <span className="detail-value">{paymentInfo.amount?.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Статус</span>
                  <span className="detail-value success">Оплачено</span>
                </div>
              </div>
            )}
            <button className="payment-button primary" onClick={handleGoToDashboard}>
              Вернуться в личный кабинет
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="payment-state">
            <div className="payment-icon error">❌</div>
            <h2>Не удалось подтвердить платеж</h2>
            <p>{message}</p>
            <div className="payment-actions">
              <button className="payment-button primary" onClick={handleGoToDashboard}>
                Вернуться в личный кабинет
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
